import React, { useState, useEffect } from "react";
import {
  Modal,
  Select,
  Button,
  Form,
  Row,
  Col,
  Input,
  Divider,
  message,
} from "antd";
import { getIcdCodes } from "../apis/SchemeService/ECHS/echsService";

const { Option } = Select;

interface DiagnosisOption {
  id: any;
  icdCode: any;
  icdDescription: any;
  parentIcdId: any;
  levelNumber: any;
  parentIcdCode: any;
  filterOn: any;
}

interface DiagnosisSelectorModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: any;
  hospitalId: any;
  schemeId: any;
}

const DiagnosisSelectorModal: React.FC<DiagnosisSelectorModalProps> = ({
  open,
  onCancel,
  onSave,
  hospitalId,
  schemeId,
}) => {
  const [dropdownData, setDropdownData] = useState<DiagnosisOption[][]>([
    [],
    [],
    [],
    [],
  ]);
  const [selectedItems, setSelectedItems] = useState<
    (DiagnosisOption | null)[]
  >([null, null, null, null]);
  const [loading, setLoading] = useState(false);

  // Fetch ICD options by parentIcdId
  const fetchIcdOptions = async (parentIcdId: number | null, level: number) => {
    try {
      setLoading(true);
      const payload = {
        hospitalId,
        schemeId,
        parentIcdId,
      };

      const response = await getIcdCodes(payload);

      if (Array.isArray(response)) {
        const updated = [...dropdownData];
        updated[level] = response;
        setDropdownData(updated);
      } else {
        message.error("Unexpected data format received from server.");
      }
    } catch (error) {
      console.error("Error fetching ICD list:", error);
      message.error("Failed to load ICD options.");
    } finally {
      setLoading(false);
    }
  };

  // Reset everything when modal opens
  useEffect(() => {
    if (open) {
      setDropdownData([[], [], [], []]);
      setSelectedItems([null, null, null, null]);
      fetchIcdOptions(null, 0);
    } else {
      setDropdownData([[], [], [], []]);
      setSelectedItems([null, null, null, null]);
    }
  }, [open]);

  const handleSelect = (id: number, level: number) => {
    const selectedOption =
      dropdownData[level]?.find((opt) => opt?.id === id) || null;

    // Update selection
    const updatedSelections = [...selectedItems];
    updatedSelections[level] = selectedOption;

    // Clear lower levels
    for (let i = level + 1; i < 4; i++) {
      updatedSelections[i] = null;
      const newData = [...dropdownData];
      newData[i] = [];
      setDropdownData(newData);
    }
    setSelectedItems(updatedSelections);

    // Fetch next level
    if (selectedOption) {
      fetchIcdOptions(selectedOption?.id, level + 1);
    }
  };

  const handleClear = (level: number) => {
    const updatedSelections = [...selectedItems];
    for (let i = level; i < 4; i++) {
      updatedSelections[i] = null;
      const newData = [...dropdownData];
      newData[i] = [];
      setDropdownData(newData);
    }
    setSelectedItems(updatedSelections);
  };

  //   const handleSave = () => {
  //     const lastSelected = [...selectedItems]
  //       .reverse()
  //       .find((item) => item !== null);
  //     if (!lastSelected) {
  //       message.error("Please select at least one ICD.");
  //       return;
  //     }
  //     onSave(lastSelected.icdDescription, lastSelected.icdCode);
  //     // onSave({
  //     //   icdCode: lastSelected.icdCode,
  //     //   icdDescription: lastSelected.icdDescription,
  //     // });
  //     onCancel();
  //   };

  const handleSave = () => {
    const firstSelected = selectedItems?.find((item) => item !== null);
    if (!firstSelected) {
      message.error("Please select at least one ICD.");
      return;
    }
    onSave(firstSelected?.icdDescription, firstSelected?.icdCode);
    onCancel();
  };

  // Render row for each level
  const renderRow = (level: number, label: string) => (
    <div style={{ marginBottom: 12 }}>
      <Row gutter={[12, 8]} align="middle">
        <Col span={1}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "rgb(0, 76, 143)",
              color: "white",
              fontWeight: 500,
              textAlign: "center",
              lineHeight: "24px",
            }}
          >
            {level + 1}
          </div>
        </Col>

        <Col span={19}>
          <Form.Item label={label} style={{ marginBottom: 0 }}>
            <Select
              placeholder={`Select ${label}`}
              value={selectedItems[level]?.id}
              allowClear
              onClear={() => handleClear(level)}
              onChange={(value) => handleSelect(value, level)}
              disabled={level > 0 && dropdownData[level]?.length === 0}
              loading={loading}
              size="large"
              showSearch
              optionFilterProp="children"
            >
              {dropdownData[level]?.map((item) => (
                <Option key={item?.id} value={item?.id}>
                  {item?.icdDescription}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item label="ICD Code" style={{ marginBottom: 0 }}>
            <Input
              value={selectedItems[level]?.icdCode || ""}
              placeholder="ICD Code"
              disabled
            />
          </Form.Item>
        </Col>
      </Row>

      {level < 3 && <Divider style={{ margin: "12px 0" }} />}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <div style={{ fontWeight: 600, fontSize: 16 }}>Select ICD Codes</div>
      }
      footer={null}
      width={900}
      centered
      destroyOnClose
    >
      <Form layout="vertical">
        {renderRow(0, "Primary ICD")}
        {renderRow(1, "Secondary ICD")}
        {renderRow(2, "Tertiary ICD")}
        {renderRow(3, "Quaternary ICD")}

        <Divider style={{ margin: "16px 0" }} />

        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onCancel}>Cancel</Button>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={handleSave}
              style={{
                backgroundColor: "rgb(0, 76, 143)",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
              }}
            >
              Save
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default DiagnosisSelectorModal;
